import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  FileText, Plus, Pencil, Trash2, Eye, Download, Copy, RefreshCw, 
  Sparkles, Building2, Rocket, Landmark
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const categoryIcons = {
  general: FileText,
  enterprise: Building2,
  startup: Rocket,
  government: Landmark
};

const categoryColors = {
  general: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700',
  startup: 'bg-green-100 text-green-700',
  government: 'bg-amber-100 text-amber-700'
};

export default function ProposalTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'general',
    content: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get('/proposal-templates');
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefault = async () => {
    try {
      setSaving(true);
      await axios.post('/proposal-templates/seed-default');
      toast.success('Default proposal template created');
      await fetchTemplates();
    } catch (error) {
      toast.error('Failed to seed default template');
    } finally {
      setSaving(false);
    }
  };

  const openNewDialog = () => {
    setEditingTemplate(null);
    setForm({
      name: '',
      description: '',
      category: 'general',
      content: getDefaultContent()
    });
    setShowDialog(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      description: template.description || '',
      category: template.category || 'general',
      content: template.content
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`/proposal-templates/${editingTemplate.id}`, form);
        toast.success('Template updated');
      } else {
        await axios.post('/proposal-templates', form);
        toast.success('Template created');
      }
      setShowDialog(false);
      await fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`/proposal-templates/${templateId}`);
      toast.success('Template deleted');
      await fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post('/proposal-templates', {
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        content: template.content
      });
      toast.success('Template duplicated');
      await fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleDownload = (template) => {
    const blob = new Blob([template.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/\s+/g, '_')}_proposal.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const getDefaultContent = () => {
    return `<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; padding: 40px; }
        .header { text-align: center; margin-bottom: 30px; }
        .section { margin-bottom: 25px; }
        .section-title { font-size: 18px; font-weight: bold; border-bottom: 2px solid #1e3a5f; padding-bottom: 5px; }
    </style>
</head>
<body>
    <div class="header">
        <h1>JustVerified</h1>
        <p>Background Verification Services Proposal</p>
    </div>
    
    <div class="section">
        <div class="section-title">Introduction</div>
        <p>Enter your proposal content here...</p>
    </div>
</body>
</html>`;
  };

  const CategoryIcon = ({ category }) => {
    const Icon = categoryIcons[category] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Proposal Templates</h1>
          <p className="text-slate-500">Create and manage service proposal templates for clients</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSeedDefault}
            disabled={saving}
            data-testid="seed-default-btn"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
            Create Default Template
          </Button>
          <Button
            onClick={openNewDialog}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No Templates Yet</h3>
            <p className="text-slate-500 mb-4">Create your first proposal template or use the default one</p>
            <Button onClick={handleSeedDefault} className="bg-indigo-600 hover:bg-indigo-700">
              <Sparkles className="w-4 h-4 mr-2" />
              Create Default Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow" data-testid={`template-card-${template.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${categoryColors[template.category] || categoryColors.general}`}>
                      <CategoryIcon category={template.category} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <Badge variant="outline" className="text-xs mt-1">
                        {template.category}
                      </Badge>
                    </div>
                  </div>
                  {template.is_default && (
                    <Badge className="bg-amber-100 text-amber-700">Default</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                  {template.description || 'No description'}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    {new Date(template.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPreviewTemplate(template)}
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(template)}
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(template)}
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(template)}
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Enterprise Services Proposal"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="startup">Startup</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this template"
              />
            </div>
            
            <div>
              <Label>Content (HTML)</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter HTML content..."
                rows={15}
                className="font-mono text-sm"
                data-testid="template-content-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-template-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview: {previewTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="border rounded-lg overflow-hidden bg-white" style={{ height: '70vh' }}>
            <iframe
              srcDoc={previewTemplate?.content}
              title="Template Preview"
              className="w-full h-full"
              sandbox="allow-same-origin"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDownload(previewTemplate)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download HTML
            </Button>
            <Button onClick={() => setPreviewTemplate(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
